import { cn, formatDateValue } from "@/lib/utils";
import { FieldDataType, HeaderDataType, OptionsResponse } from "@/types/dynamic-detail";
import { renderField } from "@/utils/field-renderer";
import * as LucideIcons from "lucide-react";
import React from "react";

interface DynamicHeaderProps {
    header: HeaderDataType;
    className?: string;
    metadata: OptionsResponse;
}

export function DynamicHeader({ header, metadata, className }: DynamicHeaderProps) {
    // Récupérer l'icône dynamiquement
    const IconComponent = metadata.model_metadata?.icon && (LucideIcons as any)[metadata.model_metadata.icon];
    const IconColor = metadata.model_metadata?.color || "bg-gray-500";

    // Formater le sous-titre si c'est une date
    const formatSubtitle = (subtitle: string) => {
        if (!subtitle) return null;

        // Si ça ressemble à une date ISO
        if (typeof subtitle === "string" && subtitle.match(/^\d{4}-\d{2}-\d{2}/)) {
            return `Created on ${formatDateValue(new Date(subtitle), "long")}`;
        }

        return subtitle;
    };

    // Rendu des champs rapides
    const renderQuickField = (field: FieldDataType) => {
        return renderField(field, {
            layout: "flex",
            showLabel: true,
            labelClassName: "truncate text-sm text-gray-500",
            valueClassName: "text-base",
            containerClassName: "flex w-full min-w-0 flex-col items-start",
            showUnit: false,
            isHeaderField: true
        });
    };

    return (
        <div className={cn("flex w-full flex-col items-start rounded-md  bg-white pb-2 pt-4 shadow-md", className)}>
            <div className={cn("flex w-full flex-row gap-2 border-b pb-4", header.subtitle ? "items-start" : "items-center")}>
                <div
                    className={cn("mx-4 mr-2 flex h-12 w-12 flex-shrink-0 items-center justify-between rounded", header.subtitle ? "mt-1" : "")}
                    style={{
                        backgroundColor: IconColor
                    }}
                >
                    {IconComponent && <IconComponent className="mx-auto flex h-8 w-8 text-center text-white" strokeWidth={1.5} />}
                </div>
                <div className={cn("flex min-w-0 flex-1 flex-col", header.subtitle ? "items-start" : "items-start justify-center")}>
                    <div className="w-full truncate pr-4 text-2xl font-bold text-gray-800">{header.title}</div>
                    {header.subtitle && <p className="text-sm text-gray-500">{formatSubtitle(header.subtitle)}</p>}
                </div>
            </div>
            {header.quick_fields && header.quick_fields.length > 0 && (
                <div className="mx-4 flex flex-row flex-wrap gap-x-6 pb-2 pt-2 sm:gap-x-8">
                    {header.quick_fields.map((field, index) => (
                        <React.Fragment key={`${field.field_name}-${index}`}>
                            <div className="min-w-0 max-w-[180px] flex-shrink-0 overflow-hidden">{renderQuickField(field)}</div>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}
